import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const Item = forwardRef((props, ref) => {
  function preventBlurring(evt) {
    evt.preventDefault();
  }

  // By default, we want clicking on a toolbar item button to _not_ blur the editor. For example,
  // if you're hitting the "Bold" button, you want to be able to keep typing. Calling
  // evt.preventDefault() in `onMouseDown` helps achieve this. We also do it in `onPointerDown`
  // because iOS doesn't reliably honor the mouse-event preventDefault, which would let focus leave
  // the editor and force a programmatic refocus that pops the native selection menu.
  const onMouseDown = props.onMouseDown || preventBlurring;
  // Canceling pointerdown suppresses the compatibility `mousedown` event. Buttons that drive their
  // behavior from `onMouseDown` (e.g. the link button's open handler, the AI buttons' hide-tooltip
  // handler) rely on that mousedown firing, so only apply the blur-prevention to pointerdown when the
  // button doesn't supply its own mousedown handler. Those buttons already preventDefault in their own
  // mousedown handler, so the editor still isn't blurred.
  const onPointerDown = props.onPointerDown || (props.onMouseDown ? undefined : preventBlurring);
  return (
    <StyledItem
      disabled={props.isDisabled}
      {...props}
      onMouseDown={onMouseDown}
      onPointerDown={onPointerDown}
      ref={ref}
    />
  );
});

Item.propTypes = {
  isDisabled: PropTypes.bool,
  onMouseDown: PropTypes.func,
  onPointerDown: PropTypes.func,
};

export { Item, activeItemStyles, disabledItemStyles, itemColorStyles, itemSizeStyles };

const itemColorStyles = css`
  background-color: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.gray800};
  // Gate hover behind a pointer-capability query: touch devices emulate :hover by leaving it applied to the
  // last-tapped element until another tap, which makes toolbar buttons look stuck "held down" after a tap. Only
  // apply hover styling where a real hovering pointer exists (mouse/trackpad); on touch the resting/active state shows.
  @media (hover: hover) {
    &:hover {
      color: ${p => p.theme.colors.gray900};
      background-color: ${p => p.theme.colors.gray100};
    }
  }
  &:active {
    color: ${p => p.theme.colors.gray900};
    background-color: ${p => p.theme.colors.gray200};
  }
  // Dark mode: the toolbar sits on a dark surface, so resting must blend and the highlight goes lighter, not darker.
  [data-theme='dark'] & {
    background-color: transparent;
    @media (hover: hover) {
      &:hover {
        background-color: ${p => p.theme.colors.gray300};
      }
    }
    &:active {
      background-color: ${p => p.theme.colors.gray400};
    }
  }
`;

const itemSizeStyles = css`
  height: 24px;
  width: 24px;
`;

const StyledItem = styled.button`
  align-items: center;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  margin-bottom: 0;
  text-align: center;

  ${itemColorStyles};
  ${itemSizeStyles};

  ${p => p.isActive && activeItemStyles};
  ${p => p.disabled && disabledItemStyles};
`;

const activeItemStyles = css`
  background-color: ${p => p.theme.colors.gray200};
  color: ${p => p.theme.colors.gray900};
  &:active {
    background-color: ${p => p.theme.colors.gray300};
  }
  // See itemColorStyles: keep the hover shade off touch devices so an active button doesn't appear stuck "held down".
  @media (hover: hover) {
    &:hover {
      background-color: ${p => p.theme.colors.gray300};
    }
  }
  // Dark mode: highlight the active button by going lighter than the dark toolbar surface.
  [data-theme='dark'] & {
    background-color: ${p => p.theme.colors.gray300};
    &:active {
      background-color: ${p => p.theme.colors.gray400};
    }
    @media (hover: hover) {
      &:hover {
        background-color: ${p => p.theme.colors.gray400};
      }
    }
  }
`;

const disabledItemStyles = css`
  background-color: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.gray300};
  cursor: default;

  &:active,
  &:hover {
    background-color: ${p => p.theme.colors.white};
    color: ${p => p.theme.colors.gray300};
  }
  // Dark mode: blend the disabled button into the toolbar instead of showing a stray fill.
  [data-theme='dark'] & {
    background-color: transparent;
    &:active,
    &:hover {
      background-color: transparent;
    }
  }
`;
